import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { FileClock, Eye } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const sisaBadge = (sisaHari) => {
  if (sisaHari === null || sisaHari === undefined) return <Badge className="bg-gray-100 text-gray-500" data-testid="kontrak-badge">-</Badge>;
  if (sisaHari < 0) {
    return <Badge className="bg-red-100 text-red-700" data-testid="kontrak-badge">Lewat {Math.abs(sisaHari)} hari</Badge>;
  }
  if (sisaHari <= 30) {
    return <Badge className="bg-amber-100 text-amber-700" data-testid="kontrak-badge">{sisaHari} hari</Badge>;
  }
  return <Badge className="bg-green-100 text-green-700" data-testid="kontrak-badge">{sisaHari} hari</Badge>;
};

const KontrakPage = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get('/employees/kontrak/akan-berakhir?hari=30');
        setEmployees(response.data);
      } catch (error) {
        console.error('Failed to fetch kontrak data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kontrak-page">
      <div>
        <h1 className="text-2xl font-bold text-foreground font-['Manrope'] tracking-tight">
          Kontrak Akan Berakhir
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Karyawan aktif dengan kontrak berakhir dalam 30 hari ke depan (termasuk yang sudah lewat)
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {employees.length === 0 ? (
            <div className="text-center py-12" data-testid="kontrak-empty-state">
              <FileClock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">Tidak ada kontrak yang akan berakhir dalam 30 hari</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="kontrak-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Karyawan</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Tanggal Berakhir</TableHead>
                    <TableHead>Sisa Kontrak</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id} data-testid={`kontrak-row-${emp.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={emp.foto_url} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {emp.nama_lengkap.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{emp.nama_lengkap}</p>
                            <p className="text-sm text-muted-foreground">{emp.position_nama}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{emp.site_nama || '-'}</TableCell>
                      <TableCell>{formatDate(emp.tanggal_berakhir_kontrak)}</TableCell>
                      <TableCell>{sisaBadge(emp.sisa_hari_kontrak)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/employees/${emp.id}`)}
                          data-testid={`kontrak-view-btn-${emp.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default KontrakPage;
